import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  Invoice,
  CreateInvoiceData,
  UpdateInvoiceData,
  CopyInvoiceData,
  SendInvoiceData,
  InvoicesResponse,
  InvoiceResponse,
  GetInvoicesParams,
  InvoicesResult,
} from '../types/invoice';
import { parseContentDispositionFilename } from './contentDisposition';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildInvoicesQuery = (params?: GetInvoicesParams): string => {
  if (!params) return '';

  const query = new URLSearchParams();

  if (params.customer_id != null) {
    query.set('customer_id', String(params.customer_id));
  }
  if (params.status != null) {
    const status = Array.isArray(params.status) ? params.status.join(',') : params.status;
    query.set('status', status);
  }
  if (params.balance_min != null) {
    query.set('balance_min', String(params.balance_min));
  }
  if (params.start_date) {
    query.set('start_date', params.start_date);
  }
  if (params.end_date) {
    query.set('end_date', params.end_date);
  }
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.is_overdue != null) {
    query.set('is_overdue', params.is_overdue ? '1' : '0');
  }
  if (params.include_lines != null) {
    query.set('include_lines', params.include_lines ? '1' : '0');
  }
  if (params.include_customer != null) {
    query.set('include_customer', params.include_customer ? '1' : '0');
  }
  if (params.sort) {
    query.set('sort', params.sort);
  }
  if (params.per_page != null) {
    query.set('per_page', String(params.per_page));
  }
  if (params.page != null) {
    query.set('page', String(params.page));
  }

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const getInvoices = async (params?: GetInvoicesParams): Promise<InvoicesResult> => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.GET_ALL}${buildInvoicesQuery(params)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch invoices');
  }

  const result: InvoicesResponse = await response.json();
  return {
    invoices: result.data ?? [],
    meta: result.meta,
    links: result.links,
  };
};

/** Fetch every page for the given filters (use scoped filters — never unbounded). */
export const fetchAllInvoices = async (
  params: Omit<GetInvoicesParams, 'page'>
): Promise<Invoice[]> => {
  const all: Invoice[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getInvoices({ ...params, page });
    all.push(...result.invoices);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};

/** Posted invoices with open balance for one customer (all pages). */
export const getCustomerReceivableInvoices = async (
  customerId: number
): Promise<Invoice[]> =>
  fetchAllInvoices({
    customer_id: customerId,
    status: ['OPEN', 'PARTIAL'],
    balance_min: 0.01,
    include_lines: false,
    include_customer: false,
    sort: 'txn_date',
    per_page: 100,
  });

/** Open/partial invoices with outstanding balance (all pages). */
export const getOpenInvoices = async (
  params: Omit<GetInvoicesParams, 'status' | 'balance_min' | 'page'> = {}
): Promise<Invoice[]> =>
  fetchAllInvoices({
    ...params,
    status: ['OPEN', 'PARTIAL'],
    balance_min: 0.01,
    include_lines: false,
    sort: params.sort ?? 'due_date',
    per_page: params.per_page ?? 100,
  });

/**
 * @deprecated Use `getInvoices` or `fetchAllInvoices` with explicit filters.
 * Unfiltered fetch only returns the first page and is unsafe at scale.
 */
export const getAllInvoices = async (): Promise<Invoice[]> => {
  const result = await getInvoices({ per_page: 100, page: 1, include_lines: false });
  return result.invoices;
};

export const getInvoiceById = async (id: number): Promise<Invoice> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.GET_BY_ID}/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const createInvoice = async (data: CreateInvoiceData): Promise<Invoice> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const updateInvoice = async (invoiceId: number, data: UpdateInvoiceData): Promise<Invoice> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.UPDATE}/${invoiceId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const deleteInvoice = async (invoiceId: number): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.DELETE}/${invoiceId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete invoice');
  }
};

export const postInvoice = async (invoiceId: number): Promise<Invoice> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.POST}/${invoiceId}/post`, {
    method: 'POST',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to post invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const voidInvoice = async (invoiceId: number): Promise<Invoice> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.VOID}/${invoiceId}/void`, {
    method: 'POST',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const copyInvoice = async (
  id: number,
  data?: CopyInvoiceData
): Promise<Invoice> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.COPY}/${id}/copy`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data ?? {}),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to copy invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const fetchInvoicePdf = async (
  id: number,
  fallbackFilename?: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.PDF}/${id}/pdf`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load invoice PDF');
  }

  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
    fallbackFilename ??
    `invoice-${id}.pdf`;

  return { blob, filename };
};

export const downloadInvoicePdf = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const { blob, filename } = await fetchInvoicePdf(id, fallbackFilename);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const sendInvoice = async (
  id: number,
  data?: SendInvoiceData
): Promise<Invoice> => {
  const payload: SendInvoiceData = {};
  if (data?.to?.trim()) payload.to = data.to.trim();
  if (data?.subject?.trim()) payload.subject = data.subject.trim();
  if (data?.message?.trim()) payload.message = data.message.trim();
  if (data?.cc != null) {
    if (Array.isArray(data.cc)) {
      const emails = data.cc.map((e) => e.trim()).filter(Boolean);
      if (emails.length === 1) payload.cc = emails[0];
      else if (emails.length > 1) payload.cc = emails;
    } else if (data.cc.trim()) {
      payload.cc = data.cc.trim();
    }
  }

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICES.SEND}/${id}/send`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to send invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};
